import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const QUESTION_TYPES = ["easy", "medium", "medium-hard", "hard", "hardest"];
const ANSWER_OPTIONS = ["option1", "option2", "option3", "option4"];

const panelSx = {
  background: "linear-gradient(180deg, rgba(11,22,36,0.98) 0%, rgba(8,17,29,0.98) 100%)",
  border: "1px solid rgba(126,170,220,0.16)",
  boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
};

const sectionSx = {
  background: "linear-gradient(145deg, rgba(17,35,56,0.9), rgba(10,23,39,0.92))",
  border: "1px solid rgba(126,170,220,0.14)",
  borderRadius: "14px",
};

const fieldSx = {
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.68)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#55D68E" },
  "& .MuiInputBase-input": { color: "#EDF4FF" },
  "& .MuiSelect-select": {
    color: "#EDF4FF !important",
    WebkitTextFillColor: "#EDF4FF !important",
  },
  "& .MuiSvgIcon-root": { color: "#8FA8C4" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(6,16,28,0.68)",
    "& fieldset": { borderColor: "rgba(126,170,220,0.22)" },
    "&:hover fieldset": { borderColor: "rgba(85,214,142,0.55)" },
    "&.Mui-focused fieldset": { borderColor: "#55D68E" },
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      color: "#EDF4FF",
      backgroundColor: "#102238",
      border: "1px solid rgba(126,170,220,0.2)",
      "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(85,214,142,0.12)" },
      "& .Mui-selected": { backgroundColor: "rgba(85,214,142,0.18) !important" },
    },
  },
};

const initialForm = {
  subject_id: "",
  topic_id: "",
  level_id: "",
  question_type: "medium",
  approved_status: "pending",
  question: "",
  question_hindi: "",
  option1: "",
  option1_hindi: "",
  option2: "",
  option2_hindi: "",
  option3: "",
  option3_hindi: "",
  option4: "",
  option4_hindi: "",
  answer: "option1",
  description: "",
  description_hindi: "",
  language_converted: false,
  time: 60,
};

function UpdateQuestions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigationQuery = searchParams.toString();
  const [loading, setLoading] = useState(true);
  const [savingAction, setSavingAction] = useState("");
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [navigation, setNavigation] = useState({
    enabled: false,
    previousId: "",
    previousPage: null,
    nextId: "",
    nextPage: null,
    currentPage: null,
    position: 0,
    total: 0,
  });
  const [notification, setNotification] = useState({
    open: false,
    color: "success",
    message: "",
  });

  const showNotice = (message, color = "success") => {
    setNotification({ open: true, color, message });
  };

  const loadFilteredNavigation = async (questionId) => {
    if (searchParams.get("filtered_navigation") !== "true") {
      setNavigation((current) => ({ ...current, enabled: false }));
      return;
    }

    const listPage = Math.max(1, parseInt(searchParams.get("list_page"), 10) || 1);
    const listLimit = Math.max(1, parseInt(searchParams.get("list_limit"), 10) || 20);
    const filterKeys = [
      "subject_id",
      "topic_id",
      "level_id",
      "approved_status",
      "qType",
      "language_converted",
      "for_book",
      "search",
    ];

    const fetchPage = async (page) => {
      const params = new URLSearchParams({ page: String(page), limit: String(listLimit) });
      filterKeys.forEach((key) => {
        const value = searchParams.get(key);
        if (value) params.set(key, value);
      });
      const response = await axios.get(
        `${Constants.getUrls.getAllQuestions}?${params.toString()}`
      );
      return response?.data?.data || { docs: [] };
    };

    let activePage = listPage;
    let currentPageData = await fetchPage(activePage);
    let docs = currentPageData?.docs || [];
    let currentIndex = docs.findIndex((item) => item._id === questionId);

    // When an item leaves the active filter after approval, pagination can shift by one page.
    // Check the adjacent pages so filtered navigation remains continuous at page boundaries.
    if (currentIndex < 0) {
      const adjacentPages = [listPage - 1, listPage + 1].filter((page) => page > 0);
      for (const candidatePage of adjacentPages) {
        const candidateData = await fetchPage(candidatePage);
        const candidateDocs = candidateData?.docs || [];
        const candidateIndex = candidateDocs.findIndex((item) => item._id === questionId);
        if (candidateIndex >= 0) {
          activePage = candidatePage;
          currentPageData = candidateData;
          docs = candidateDocs;
          currentIndex = candidateIndex;
          break;
        }
      }
    }

    if (currentIndex < 0) {
      setNavigation((current) => ({ ...current, enabled: false }));
      return;
    }

    let previousId = currentIndex > 0 ? docs[currentIndex - 1]._id : "";
    let previousPage = previousId ? activePage : null;
    let nextId = currentIndex < docs.length - 1 ? docs[currentIndex + 1]._id : "";
    let nextPage = nextId ? activePage : null;

    const pageRequests = [];
    if (!previousId && currentPageData.hasPrevPage) {
      pageRequests.push(
        fetchPage(activePage - 1).then((data) => {
          const previousDocs = data?.docs || [];
          previousId = previousDocs[previousDocs.length - 1]?._id || "";
          previousPage = previousId ? activePage - 1 : null;
        })
      );
    }
    if (!nextId && currentPageData.hasNextPage) {
      pageRequests.push(
        fetchPage(activePage + 1).then((data) => {
          nextId = data?.docs?.[0]?._id || "";
          nextPage = nextId ? activePage + 1 : null;
        })
      );
    }
    await Promise.all(pageRequests);

    setNavigation({
      enabled: true,
      previousId,
      previousPage,
      nextId,
      nextPage,
      currentPage: activePage,
      position: (activePage - 1) * listLimit + currentIndex + 1,
      total: currentPageData.totalDocs || 0,
    });
  };

  const loadTopics = async (subjectId) => {
    if (!subjectId) {
      setAllTopics([]);
      return [];
    }
    const response = await axios.get(
      `${Constants.getUrls.getAllTopics}?subject_id=${subjectId}&limit=1000&page=1`
    );
    const topics = response?.data?.data?.docs || [];
    setAllTopics(topics);
    return topics;
  };

  const loadLevels = async (subjectId, topicId) => {
    if (!subjectId || !topicId) {
      setAllLevels([]);
      return [];
    }
    const response = await axios.get(
      `${Constants.getUrls.getAllLevels}?subject_id=${subjectId}&topic_id=${topicId}&limit=1000&page=1`
    );
    const levels = response?.data?.data?.docs || [];
    setAllLevels(levels);
    return levels;
  };

  useEffect(() => {
    const boot = async () => {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      try {
        setLoading(true);
        const [subjectResponse, questionResponse] = await Promise.all([
          axios.get(`${Constants.getUrls.getAllSubjects}?search=&limit=1000&page=1`),
          axios.get(`${Constants.getUrls.getAllQuestions}/${id}`),
        ]);
        const data = questionResponse?.data?.data || {};
        const subjectId = data?.subject_id?._id || data?.subject_id || "";
        const topicId = data?.topic_id?._id || data?.topic_id || "";
        const levelId = data?.level_id?._id || data?.level_id || "";

        setAllSubjects(subjectResponse?.data?.data?.docs || []);
        setForm({
          subject_id: subjectId,
          topic_id: topicId,
          level_id: levelId,
          question_type: data?.question_type || "medium",
          approved_status: data?.approved_status || "pending",
          question: data?.question || "",
          question_hindi: data?.question_hindi || "",
          option1: data?.option1 || "",
          option1_hindi: data?.option1_hindi || "",
          option2: data?.option2 || "",
          option2_hindi: data?.option2_hindi || "",
          option3: data?.option3 || "",
          option3_hindi: data?.option3_hindi || "",
          option4: data?.option4 || "",
          option4_hindi: data?.option4_hindi || "",
          answer: data?.answer || "option1",
          description: data?.description || "",
          description_hindi: data?.description_hindi || "",
          language_converted: Boolean(data?.language_converted),
          time: data?.time ?? 60,
        });

        await Promise.all([
          loadTopics(subjectId),
          loadLevels(subjectId, topicId),
          loadFilteredNavigation(id),
        ]);
      } catch (error) {
        showNotice(error?.response?.data?.message || "Unable to load the question.", "error");
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, [id, navigationQuery]);

  const handleField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubjectChange = async (subjectId) => {
    setForm((current) => ({
      ...current,
      subject_id: subjectId,
      topic_id: "",
      level_id: "",
    }));
    setAllLevels([]);
    try {
      await loadTopics(subjectId);
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to load topics.", "error");
    }
  };

  const handleTopicChange = async (topicId) => {
    setForm((current) => ({ ...current, topic_id: topicId, level_id: "" }));
    try {
      await loadLevels(form.subject_id, topicId);
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to load levels.", "error");
    }
  };

  const saveQuestion = async (approveQuestion = false) => {
    const nextStatus = approveQuestion ? "approved" : form.approved_status;
    try {
      setSavingAction(approveQuestion ? "approve" : "save");
      await axios.put(`${Constants.putUrls.updateQuestion}${id}`, {
        ...form,
        approved_status: nextStatus,
        time: Number(form.time || 60),
      });
      setForm((current) => ({ ...current, approved_status: nextStatus }));
      showNotice(
        approveQuestion
          ? "Question changes saved and approved successfully."
          : "Question changes saved successfully.",
        "success"
      );
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to update the question.", "error");
    } finally {
      setSavingAction("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    saveQuestion(event.nativeEvent?.submitter?.value === "approve");
  };

  const openAdjacentQuestion = (questionId, page) => {
    if (!questionId || !page) return;
    const params = new URLSearchParams(searchParams);
    params.set("list_page", String(page));
    navigate(`/updateQuestion/${questionId}?${params.toString()}`);
  };

  const returnToQuestionList = () => {
    if (!navigationQuery) {
      navigate("/questions");
      return;
    }
    const params = new URLSearchParams(searchParams);
    if (navigation.currentPage) params.set("list_page", String(navigation.currentPage));
    navigate(`/questions?${params.toString()}`);
  };

  const answerLabel = (optionKey, index) => {
    if (!ANSWER_OPTIONS.includes(optionKey)) return "No valid correct answer selected";
    const english = form[optionKey] || "English option not entered";
    const hindi = form[`${optionKey}_hindi`] || "Hindi option not entered";
    return `Option ${index + 1}: ${english} / ${hindi}`;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ ...panelSx, overflow: "visible" }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={{ xs: 2, md: 3 }}
            borderRadius="lg"
            sx={{
              background:
                "radial-gradient(circle at 92% 12%, rgba(242,117,0,0.28), transparent 32%), linear-gradient(135deg, #123527 0%, #10243A 68%)",
              border: "1px solid rgba(85,214,142,0.2)",
              boxShadow: "0 16px 34px rgba(0,0,0,0.28)",
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <MDBox>
                <MDTypography variant="h5" color="white" fontWeight="bold">
                  Edit MCQ in English & Hindi
                </MDTypography>
                <MDTypography variant="button" sx={{ color: "rgba(255,255,255,0.72)" }}>
                  Review both languages, the correct answer, and both explanations before saving.
                </MDTypography>
              </MDBox>
              <MDButton variant="outlined" color="white" onClick={returnToQuestionList}>
                Back to Questions
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox py={3} px={{ xs: 2, md: 3 }}>
            {navigation.enabled && !loading && (
              <MDBox
                sx={sectionSx}
                p={1.5}
                mb={2.5}
                display="flex"
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                flexDirection={{ xs: "column", md: "row" }}
                gap={1.5}
              >
                <MDButton
                  type="button"
                  variant="outlined"
                  color="info"
                  disabled={!navigation.previousId || Boolean(savingAction)}
                  onClick={() =>
                    openAdjacentQuestion(navigation.previousId, navigation.previousPage)
                  }
                >
                  Previous Question
                </MDButton>
                <MDTypography variant="button" textAlign="center" sx={{ color: "#AFC1D6" }}>
                  Filtered result {navigation.position} of {navigation.total}
                </MDTypography>
                <MDButton
                  type="button"
                  variant="outlined"
                  color="info"
                  disabled={!navigation.nextId || Boolean(savingAction)}
                  onClick={() => openAdjacentQuestion(navigation.nextId, navigation.nextPage)}
                >
                  Next Question
                </MDButton>
              </MDBox>
            )}
            {loading ? (
              <MDTypography variant="button" sx={{ color: "#8FA8C4" }}>
                Loading question details...
              </MDTypography>
            ) : (
              <form onSubmit={handleSubmit}>
                <MDBox sx={sectionSx} p={{ xs: 2, md: 2.5 }} mb={2.5}>
                  <MDTypography variant="h6" color="white" mb={2}>
                    Classification
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        required
                        label="Subject"
                        value={form.subject_id}
                        onChange={(event) => handleSubjectChange(event.target.value)}
                        sx={fieldSx}
                        SelectProps={{ MenuProps: menuProps }}
                      >
                        <MenuItem value="">Select Subject</MenuItem>
                        {allSubjects.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.subject_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        required
                        label="Topic"
                        value={form.topic_id}
                        onChange={(event) => handleTopicChange(event.target.value)}
                        sx={fieldSx}
                        SelectProps={{ MenuProps: menuProps }}
                      >
                        <MenuItem value="">Select Topic</MenuItem>
                        {allTopics.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.topic_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        required
                        label="Level"
                        value={form.level_id}
                        onChange={(event) => handleField("level_id", event.target.value)}
                        sx={fieldSx}
                        SelectProps={{ MenuProps: menuProps }}
                      >
                        <MenuItem value="">Select Level</MenuItem>
                        {allLevels.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.level_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        required
                        label="Difficulty"
                        value={form.question_type}
                        onChange={(event) => handleField("question_type", event.target.value)}
                        sx={fieldSx}
                        SelectProps={{ MenuProps: menuProps }}
                      >
                        {QUESTION_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        required
                        label="Approval Status"
                        value={form.approved_status}
                        onChange={(event) => handleField("approved_status", event.target.value)}
                        sx={fieldSx}
                        SelectProps={{ MenuProps: menuProps }}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox sx={sectionSx} p={{ xs: 2, md: 2.5 }} mb={2.5}>
                  <MDTypography variant="h6" color="white" mb={0.5}>
                    Question
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#7890AC" }}>
                    English and Hindi are shown together so their meaning can be checked side by side.
                  </MDTypography>
                  <Grid container spacing={2} mt={0.25}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        minRows={4}
                        label="Question — English"
                        value={form.question}
                        onChange={(event) => handleField("question", event.target.value)}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="प्रश्न — हिन्दी"
                        value={form.question_hindi}
                        onChange={(event) => handleField("question_hindi", event.target.value)}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox sx={sectionSx} p={{ xs: 2, md: 2.5 }} mb={2.5}>
                  <MDTypography variant="h6" color="white" mb={2}>
                    Answer Options
                  </MDTypography>
                  <Grid container spacing={2}>
                    {ANSWER_OPTIONS.map((optionKey, index) => (
                      <React.Fragment key={optionKey}>
                        <Grid item xs={12} md={5}>
                          <TextField
                            fullWidth
                            required
                            label={`Option ${index + 1} — English`}
                            value={form[optionKey]}
                            onChange={(event) => handleField(optionKey, event.target.value)}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <TextField
                            fullWidth
                            label={`विकल्प ${index + 1} — हिन्दी`}
                            value={form[`${optionKey}_hindi`]}
                            onChange={(event) =>
                              handleField(`${optionKey}_hindi`, event.target.value)
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <MDButton
                            type="button"
                            fullWidth
                            variant={form.answer === optionKey ? "contained" : "outlined"}
                            color={form.answer === optionKey ? "success" : "secondary"}
                            onClick={() => handleField("answer", optionKey)}
                            sx={{ minHeight: 56, height: "100%" }}
                          >
                            {form.answer === optionKey ? "Correct Answer" : "Set Correct"}
                          </MDButton>
                        </Grid>
                      </React.Fragment>
                    ))}

                    <Grid item xs={12}>
                      <MDBox
                        px={2}
                        py={1.5}
                        borderRadius="lg"
                        sx={{
                          backgroundColor: "rgba(85,214,142,0.09)",
                          border: "1px solid rgba(85,214,142,0.3)",
                        }}
                      >
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#55D68E" }}>
                          Selected correct answer / सही उत्तर
                        </MDTypography>
                        <MDTypography variant="body2" sx={{ color: "#EDF4FF" }}>
                          {answerLabel(form.answer, ANSWER_OPTIONS.indexOf(form.answer))}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox sx={sectionSx} p={{ xs: 2, md: 2.5 }} mb={2.5}>
                  <MDTypography variant="h6" color="white" mb={0.5}>
                    Explanation
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#7890AC" }}>
                    These explanations are displayed after the question is answered.
                  </MDTypography>
                  <Grid container spacing={2} mt={0.25}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        label="Explanation — English"
                        value={form.description}
                        onChange={(event) => handleField("description", event.target.value)}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        label="व्याख्या — हिन्दी"
                        value={form.description_hindi}
                        onChange={(event) => handleField("description_hindi", event.target.value)}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox display="flex" gap={1.5} flexWrap="wrap">
                  <MDButton
                    type="submit"
                    value="save"
                    variant="outlined"
                    color="info"
                    disabled={Boolean(savingAction)}
                  >
                    {savingAction === "save" ? "Saving Edit..." : "Save Edit"}
                  </MDButton>
                  <MDButton
                    type="submit"
                    value="approve"
                    variant="contained"
                    color="success"
                    disabled={Boolean(savingAction)}
                  >
                    {savingAction === "approve" ? "Saving & Approving..." : "Save & Approve"}
                  </MDButton>
                  <MDButton
                    type="button"
                    variant="outlined"
                    color="secondary"
                    onClick={returnToQuestionList}
                    disabled={Boolean(savingAction)}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </form>
            )}
          </MDBox>

          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz Questions"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
            close={() => setNotification((current) => ({ ...current, open: false }))}
            bgWhite
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default UpdateQuestions;
